import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Certificationdetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { certifications, productType } = location.state || {};

  const handleBackClick = () => {
    navigate(-1);
  };

  // Function to determine which row should be highlighted based on Scope priority
  const getHighlightedItemCode = (cert) => {
    // Check if certification is required
    const hasYCertification = cert.changeItems.some(
      item => item.requirements['Is certification required'] === 'Y'
    );
    
    if (!hasYCertification) {
      return null; // No highlighting needed if all are 'N'
    }

    // Define scope priority (highest to lowest)
    const scopePriority = ['Full', 'Partial', 'Paper', 'Multiple'];
    
    // Find the highest priority scope that exists
    for (const scope of scopePriority) {
      const itemsWithScope = cert.changeItems.filter(
        item => item.requirements['Scope'] === scope && 
                item.requirements['Is certification required'] === 'Y'
      );
      
      if (itemsWithScope.length > 0) {
        // Return the first item's code (based on UI display order)
        return itemsWithScope[0].code;
      }
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-gray-600">
        <div className="flex items-center px-6 py-4">
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center w-12 h-12 -ml-2 mr-3 rounded-lg hover:bg-gray-800 active:bg-gray-700 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
          </button>
          <h1 className="text-2xl font-semibold text-white">
            HW Change Verification Assessment for Certification / More Details
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8 max-w-[95%] mx-auto">
        {certifications && certifications.length > 0 ? (
          <div className="space-y-8">
            {certifications.map((cert, certIndex) => {
              const certId = cert.certificationName.match(/^(\d+\.\d+)/)?.[1] || certIndex;
              const certName = cert.certificationName.replace(/^\d+\.\d+\s+/, '');
              
              // Get the code of the item that should be highlighted
              const highlightedCode = getHighlightedItemCode(cert);
              
              return (
                <div key={certIndex} className="border border-white">
                  {/* Certification Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        {/* First Header Row - Logo Certification spanning 2 columns, Cert Name spanning remaining */}
                        <tr className="bg-black border-b border-white">
                          <th 
                            colSpan="2" 
                            className="px-4 py-3 text-sm font-bold text-white border-r border-white text-center"
                          >
                            Logo Certification
                          </th>
                          <th 
                            colSpan="6" 
                            className="px-4 py-3 text-sm font-bold text-white text-center"
                          >
                            {certId} {certName}
                          </th>
                        </tr>
                        {/* Second Header Row - Column Headers */}
                        <tr className="bg-black border-b border-white">
                          <th className="px-4 py-3 text-sm font-bold text-white border-r border-white text-center w-24">
                            Code
                          </th>
                          <th className="px-4 py-3 text-sm font-bold text-white border-r border-white text-center w-64">
                            Changes
                          </th>
                          <th className="px-4 py-3 text-sm font-bold text-white border-r border-white text-center w-32">
                            {certId}.1 Is certification required
                          </th>
                          <th className="px-4 py-3 text-sm font-bold text-white border-r border-white text-center w-32">
                            {certId}.2 Scope
                          </th>
                          <th className="px-4 py-3 text-sm font-bold text-white border-r border-white text-center w-40">
                            {certId}.3 Lead time (pre-test to CG)
                          </th>
                          <th className="px-4 py-3 text-sm font-bold text-white border-r border-white text-center w-32">
                            {certId}.4 Total Expense (USD)
                          </th>
                          <th className="px-4 py-3 text-sm font-bold text-white border-r border-white text-center w-32">
                            {certId}.5 Total LAB Sample (Q'ty)
                          </th>
                          <th className="px-4 py-3 text-sm font-bold text-white text-center w-24">
                            {certId}.6 Owner
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cert.changeItems.map((item, itemIndex) => {
                          const isHighlighted = highlightedCode === item.code;
                          
                          return (
                            <tr 
                              key={itemIndex}
                              className={`border-b border-white last:border-b ${
                                isHighlighted ? 'bg-blue-600' : 'bg-black'
                              }`}
                            >
                              <td className="px-4 py-3 text-sm text-white font-medium border-r border-white text-center">
                                {item.code}
                              </td>
                              <td className="px-4 py-3 text-sm text-white border-r border-white">
                                {item.changes}
                              </td>
                              <td className="px-4 py-3 text-sm text-white border-r border-white text-center font-semibold">
                                {item.requirements['Is certification required'] || 'N'}
                              </td>
                              <td className="px-4 py-3 text-sm text-white border-r border-white text-center">
                                {item.requirements['Scope'] || 'Re-use'}
                              </td>
                              <td className="px-4 py-3 text-sm text-white border-r border-white text-center">
                                {item.requirements['Lead time(pre-test to CG)'] || 
                                 item.requirements['Lead time (pre-test to CG)'] || '0'}
                              </td>
                              <td className="px-4 py-3 text-sm text-white border-r border-white text-center">
                                {item.requirements['Total Expense (USD)'] || 
                                 item.requirements['Total Expense(USD)'] || '0'}
                              </td>
                              <td className="px-4 py-3 text-sm text-white border-r border-white text-center">
                                {item.requirements['Total LAB Sample (Q\'ty)'] || 
                                 item.requirements['Total LAB Sample (Q\'ty)'] || '0'}
                              </td>
                              <td className="px-4 py-3 text-sm text-white text-center">
                                {item.requirements['Owner'] || 'N/A'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400">No certification data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificationdetails;